import { useCallback, useState, VoidFunctionComponent } from 'react';
import { Plane, Text } from '@react-three/drei';
import { useResponsive } from '@/hooks/useResponsive';
import { Screen } from '@/components/Screen';
import { PlaystationButton } from '@/components/PlaystationButton';
import { CaptchaImage } from '@/components/CaptchaImage';
import type { BotQuizCaptchaAnswers, Captcha as CaptchaGroup } from '@/lib/types';
import { garamondNarrowLight } from '@/lib/fonts';
import { buildPublicUrl } from '@/lib/globals';
import captchaAddSound from '@/sounds/ob-sfx-captcha-add.mp3';
import captchaRemoveSound from '@/sounds/ob-sfx-captcha-remove.mp3';
import commitSound from '@/sounds/ob-sfx-commit.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';

// clear captcha results
const initCaptcha = (captcha: CaptchaGroup): CaptchaGroup => ({
    ...captcha,
    options: captcha.options.map(([url]) => [url, false]),
});

interface Props {
    captcha: CaptchaGroup;
    submitCaptcha: (answers: BotQuizCaptchaAnswers) => void;
}

export const Captcha: VoidFunctionComponent<Props> = ({ captcha, submitCaptcha }) => {
    const { responsiveValue } = useResponsive();
    const [currentCaptcha, setCurrentCaptcha] = useState<CaptchaGroup>(initCaptcha(captcha));
    const [playCaptchaAddSound] = useOnlybotsSound(captchaAddSound);
    const [playCaptchaRemoveSound] = useOnlybotsSound(captchaRemoveSound);
    const [playCommitSound] = useOnlybotsSound(commitSound);

    const toggleCaptcha = useCallback(
        (index: number) => {
            const options = currentCaptcha.options;
            if (options[index][1]) {
                playCaptchaRemoveSound();
            } else {
                playCaptchaAddSound();
            }
            setCurrentCaptcha({
                ...currentCaptcha,
                options: [
                    ...options.slice(0, index),
                    [options[index][0], !options[index][1]],
                    ...options.slice(index + 1),
                ],
            });
        },
        [currentCaptcha, playCaptchaAddSound, playCaptchaRemoveSound]
    );

    return (
        <Screen>
            <Plane position={[0, responsiveValue(2.2, 2.95), 0]} material-color="#68F" args={[7.5, 1.0]}>
                <Text
                    textAlign="center"
                    font={garamondNarrowLight}
                    fontSize={responsiveValue(0.4, 0.3)}
                    color="white"
                    maxWidth={responsiveValue(8, 4)}
                >
                    {currentCaptcha.prompt}
                </Text>
            </Plane>
            <group scale={responsiveValue([1.15, 1.15, 1], [1.15, 1.15, 1])}>
                <group position={[-1, responsiveValue(1.1, 1), 0]}>
                    {currentCaptcha.options.map(([fn], i) => {
                        const captchaSelected = currentCaptcha.options[i][1];
                        return (
                            <CaptchaImage
                                key={i}
                                index={i}
                                url={buildPublicUrl(`/img/captcha/${fn}`)}
                                onClick={() => toggleCaptcha(i)}
                                selected={captchaSelected}
                            />
                        );
                    })}
                </group>
            </group>
            <PlaystationButton
                clickableAreaDimensions={[2.2, 0.7]}
                position={[responsiveValue(-0.5, -0.4), responsiveValue(-2.5, -3.1), 0]}
                action={() => {
                    playCommitSound();
                    submitCaptcha({
                        id: currentCaptcha.id,
                        answers: currentCaptcha.options,
                    });
                }}
                icon="square"
                label="DONE"
            />
        </Screen>
    );
};
